import { flatten, mergeDeepLeft } from "ramda";
import React from "react";
import { useTranslation } from "react-i18next";
import { getMonthName, getWeeksPeriodText } from "../../averagePrices/date";
import getWeeksInChronologicalOrder from "../../averagePrices/PriceChart/getWeeksInChronologicalOrder";
import PriceChart, {
  availablePeriods,
  Period,
} from "../../averagePrices/PriceChart/PriceChart";
import { Poultry } from "../../averagePrices/types";
import { useMarketPrices } from "./api";

const getProductWithRegionName = (prodId, region, t, language) =>
  `${t(Poultry[prodId])} ${
    language === "pl" ? region.shortcut : region.shortcut_en
  }`;

const getAllProducts = (settings, t, language) => {
  const wholeCountry = [
    {
      id: Poultry.kurczeta,
      key: t(Poultry[Poultry.kurczeta]),
      color: "#E92B5C",
    },
    {
      id: Poultry.indory,
      key: t(Poultry[Poultry.indory]),
      color: "#FFA203",
    },
    {
      id: Poultry.indyczki,
      key: t(Poultry[Poultry.indyczki]),
      color: "#0CDC8F",
    },
    {
      id: Poultry.kaczki,
      key: t(Poultry[Poultry.kaczki]),
      color: "#FFE503",
    },
    { id: Poultry.gesi, key: t(Poultry[Poultry.gesi]), color: "#0BD7DC" },
    {
      id: Poultry.kury,
      key: t(Poultry[Poultry.kury]),
      color: "#0001FF",
    },
  ];

  if (!settings) {
    return wholeCountry;
  }

  const { products, regions } = settings;

  const selectedProducts = products.map(prod =>
    wholeCountry.find(data => data.id === prod.id)
  );

  return flatten(
    regions.map(region =>
      selectedProducts.map(prod => ({
        ...prod,
        key: getProductWithRegionName(prod.id, region, t, language),
      }))
    )
  );
};

const getChartData = ({ settings, dataList, option, i18n, getProductName }) => {
  const { period, weekCount } = option;

  const getProductsMap = products =>
    products.reduce(
      (prev, product) => ({
        ...prev,
        [getProductName(product.product_id)]: product.price
          ? product.price.toFixed(2)
          : null,
      }),
      {}
    );

  const filterProducts = products => {
    if (!settings) {
      return products;
    }
    return products.filter(p =>
      settings.products.some(({ id }) => id === p.product_id)
    );
  };

  if (period === Period.weekly) {
    const weeks = getWeeksInChronologicalOrder(dataList, weekCount);

    return [...weeks].reverse().map(week => ({
      name: getWeeksPeriodText(week.start_date, week.end_date, i18n.language),
      ...getProductsMap(filterProducts(week.products)),
    }));
  }

  return [...dataList].reverse().map(month => ({
    name: getMonthName(new Date(month.start_date), i18n.language),
    ...getProductsMap(filterProducts(month.products)),
  }));
};

const PriceChartSection = ({ settings, currentView, title }) => {
  const { t, i18n } = useTranslation(["averagePrices"]);
  const [option, setOption] = React.useState(
    availablePeriods[availablePeriods.length - 1]
  );
  const { data: marketPrices } = useMarketPrices(option.period, settings);

  if (!marketPrices) {
    return <PriceChart.Loader />;
  }

  let selectedProducts =
    currentView > 0
      ? getAllProducts(settings, t, i18n.language).filter(
          ({ id }) => id === currentView
        )
      : getAllProducts(settings, t, i18n.language);

  let mappedData = [];
  if (settings) {
    selectedProducts = getAllProducts(settings, t, i18n.language);

    mappedData = Object.values(
      marketPrices.groups.reduce((prev, prices) => {
        const matchingRegion = settings.regions.find(
          reg => reg.id === prices.id
        );
        return mergeDeepLeft(
          prev,
          getChartData({
            settings,
            dataList: prices.periods,
            option,
            i18n,
            getProductName: id =>
              getProductWithRegionName(id, matchingRegion, t, i18n.language),
          })
        );
      }, {})
    );
  } else {
    mappedData = getChartData({
      dataList: marketPrices.groups[0].periods,
      option,
      i18n,
      getProductName: id => t(Poultry[id]),
    });
  }

  return (
    <PriceChart
      option={option}
      onOptionChange={setOption}
      chartData={mappedData}
      selectedProducts={selectedProducts}
      typeTitle={title}
    />
  );
};

export default PriceChartSection;
